import {observer} from 'mobx-react-lite';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';
import {matchPath, useLocation, useNavigate} from 'react-router-dom';
import {useRecoilValue} from 'recoil';
import {ActionItem, EButton, EHeaderButton} from 'webapp-lib/Components';
import {truncate} from 'webapp-lib/utils';

import {Dialog} from '#';
import {Add, User} from '_/assets/images';
import {useDialogController, useSafeCallback} from '_/hooks';
import {useFeatureFlagCheck} from '_/hooks/useFeatureFlagCheck';
import {usePermissionChecker} from '_/hooks/usePermissionChecker';
import {useRouteNavigate} from '_/hooks/useRouteNavigate';
import i18n from '_/i18n';
import {casusPathState} from '_/state/casusPathState';
import {failedMailAuthentication} from '_/state/failedMailAuthentication';
import {languageState} from '_/state/language';
import {userState} from '_/state/user';
import {GlobalTabBarItem} from '_/store/tabBar.store';
import {getRouteUrl, isElectron, TabBarContext} from '_/utilities';
import {GlobalPermissions, GlobalPermissionsType} from '_/utilities/types';

import CreateDocumentFromTemplate from '../../modules/documents/screens/CreateDocumentFromTemplate/CreateDocumentFromTemplate';
import Notifications from '../../modules/notifications';
import {TimetrackerButton} from '../../modules/timetracker/components/TimetrackerButton';

import {BackupShelf} from './BackupShelf/BackupShelf';
import {useBackupShelfFiles} from './BackupShelf/BackupShelf.utils';
import {CasusTab, CasusTabBar} from './CasusTabBar/CasusTabBar';
import {FileShelf} from './FileShelf/FileShelf';
import {LeftSide, StyledHeader} from './Header.styles';
import {UploadQueue} from './UploadQueue';
import VersionInformation from './VersionInformation';

type HeaderProps = {
  activeModule: string;
  onLogout: () => void;
  onQuit: () => void;
  onShowFilesInUse: () => void;
};

type AddButtonOption = ActionItem & {
  id: string;
  section?: number;
  subActions?: AddButtonOption[];
  flipSubMenuPopupPosition?: boolean;
  requiredPermission?: GlobalPermissionsType;
};

enum SidebarType {
  NOTIFICATIONS = 'NOTIFICATIONS',
  FILE_SHELF = 'SHELF',
  BACKUP_SHELF = 'BACKUP_SHELF',
  UPLOADING_DOCUMENTS = 'UPLOADING_DOCUMENTS',
  TIMETRACKER = 'TIMETRACKER',
}

const isCasusSubscreen = (currentPath: string): boolean => {
  const casusUrl = getRouteUrl({module: 'casus'});
  if (!currentPath.startsWith(casusUrl)) {
    return false;
  }
  return currentPath !== casusUrl;
};

// eslint-disable-next-line max-statements
const UHeader = ({
  activeModule,
  onLogout,
  onQuit,
  onShowFilesInUse,
}: HeaderProps): React.ReactElement => {
  const {t} = useTranslation('common');
  const {safeCallback} = useSafeCallback();
  const [showDebugOptions, setShowDebugOptions] = useState(false);
  const {
    isVisible: isVersionInformationVisible,
    showDialog: showVersionInformationDialog,
    closeDialog: closeVersionInformationDialog,
  } = useDialogController();
  const {
    isVisible: isReportDialogVisible,
    showDialog: showReportDialog,
    closeDialog: closeReportDialog,
  } = useDialogController();
  const [activeSidebar, setActiveSidebar] = useState<SidebarType>();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const {goToCasusDocuments, goToAllEmails, goToContacts} = useRouteNavigate();
  const user = useRecoilValue(userState);
  const language = useRecoilValue(languageState);
  const hasFailedMailAuthentication = useRecoilValue(failedMailAuthentication);
  const {tabBarItems, getTabByPathname, clearTabBarItems} =
    useContext(TabBarContext);
  const {checkPermission} = usePermissionChecker();
  const shouldShowTimeTracker = useFeatureFlagCheck('timeTracker');

  const casusId = matchPath('/casus/:casusId/*', pathname)?.params?.casusId;

  const createMenuItems: Record<string, AddButtonOption[]> = useMemo(
    () => ({
      casus: [
        {
          id: 'casus',
          text: t('topBar.createNewMenu.casus'),
          requiredPermission: GlobalPermissions.Casus.CreateCasus,
          subActions: [
            {
              callback: safeCallback((): void =>
                navigate(
                  getRouteUrl({
                    module: 'casus',
                    action: 'create-legal-entity-casus',
                  })
                )
              ),
              id: 'create-legal-entity-casus',
              text: t('casus:createCasus.forLegalEntity'),
            },
            {
              callback: safeCallback((): void =>
                navigate(
                  getRouteUrl({
                    module: 'casus',
                    action: 'create-natural-person-casus',
                  })
                )
              ),
              id: 'create-natural-person-casus',
              text: t('casus:createCasus.forNaturalPerson'),
            },
            {
              callback: safeCallback((): void =>
                navigate(
                  getRouteUrl({
                    module: 'casus',
                    action: 'create-pseudonym-casus',
                  })
                )
              ),
              id: 'create-pseudonym-casus',
              text: t('casus:createCasus.forPseudonym'),
            },
          ],
        },
      ],
      contacts: [
        {
          id: 'contacts',
          subActions: [
            {
              callback: safeCallback((): void =>
                goToContacts({action: 'create-legal-entity'})
              ),
              id: 'create-legal-entity',
              text: t('topBar.createNewMenu.legalEntity'),
            },
            {
              callback: safeCallback((): void =>
                goToContacts({action: 'create-natural-person'})
              ),
              id: 'create-natural-person',
              text: t('topBar.createNewMenu.naturalPerson'),
            },
          ],
          text: t('topBar.createNewMenu.contact'),
          flipSubMenuPopupPosition: true,
        },
      ],
      ...(casusId
        ? {
            documents: [
              {
                callback: safeCallback(
                  (): void =>
                    goToCasusDocuments({
                      casusId,
                      action: 'create-document-from-template',
                    }),
                  {id: 'create-document'}
                ),
                id: 'documents',
                text: t('topBar.createNewMenu.document'),
              },
            ],
          }
        : {}),
      ...(isElectron()
        ? {
            communication: [
              {
                callback: safeCallback(
                  (): void => goToAllEmails({action: 'create-email'}),
                  {id: 'create-email'}
                ),
                id: 'communication',
                text: t('topBar.createNewMenu.email'),
              },
            ],
          }
        : {}),
      'time-tracking': [
        {
          callback: safeCallback((): void =>
            navigate(
              getRouteUrl({
                module: 'time-tracking',
                dateValue: moment().startOf('day').valueOf(),
                action: 'create-activity',
              })
            )
          ),
          id: 'time-tracking',
          text: t('topBar.createNewMenu.worklog'),
        },
      ],
      reporting: [
        {
          callback: safeCallback((): void => showReportDialog()),
          id: 'create-report',
          text: t('topBar.createNewMenu.createReport'),
        },
      ],
    }),
    [
      t,
      safeCallback,
      casusId,
      navigate,
      goToContacts,
      goToCasusDocuments,
      goToAllEmails,
      showReportDialog,
    ]
  );

  const addMenuItems: ActionItem[] = [
    ...(activeModule && activeModule in createMenuItems
      ? createMenuItems[activeModule].filter((menuItem) =>
          checkPermission(menuItem.requiredPermission)
        )
      : []),
    ...Object.entries(createMenuItems)
      .filter(([key]) => key !== activeModule)
      .map(([_, value]) => value)
      .flat()
      .filter((menuItem) => checkPermission(menuItem.requiredPermission))
      .map((item) => ({...item, section: 1})),
  ];

  const toggleTranslation = () => {
    if (i18n.language === 'cimode') {
      i18n.changeLanguage(language);
      return;
    }
    i18n.changeLanguage('cimode');
  };

  const versionInformationDialog = (
    <Dialog
      actions={[
        ...(showDebugOptions
          ? [
              <EButton key='toggleTranslation' onClick={toggleTranslation}>
                {t('common:actions.toggleTranslationKeys')}
              </EButton>,
            ]
          : []),
        <EButton key='cancel' autoFocus onClick={closeVersionInformationDialog}>
          {t('common:actions.close')}
        </EButton>,
      ]}
      headerText={t('topBar.userMenu.versionInfo')}
      isShown={isVersionInformationVisible}
      onClose={closeVersionInformationDialog}
    >
      <VersionInformation showDebugToggle={() => setShowDebugOptions(true)} />
    </Dialog>
  );

  const userMenuActions = useMemo(
    () => [
      {
        callback: showVersionInformationDialog,
        text: t('topBar.userMenu.versionInfo'),
        id: 'version-information',
      },
      {
        callback: (): void => navigate(getRouteUrl({module: 'user-settings'})),
        text: t('topBar.userMenu.userSettings'),
        id: 'userSettings',
      },
      {
        callback: onShowFilesInUse,
        text: t('topBar.userMenu.documents-in-use'),
        id: 'documents-in-use',
      },
      {
        callback: safeCallback(onLogout),
        text: truncate(
          t('topBar.userMenu.logout', {
            userGivenNames: user.givenNames,
            userLastNames: user.lastNames,
          }),
          30
        ),
        id: 'logout',
        section: 1,
      },
      ...(isElectron()
        ? [
            {
              callback: safeCallback(onQuit),
              text: t('topBar.userMenu.quit'),
              id: 'quit',
              section: 2,
            },
          ]
        : []),
    ],
    [
      showVersionInformationDialog,
      t,
      onShowFilesInUse,
      safeCallback,
      onLogout,
      user.givenNames,
      user.lastNames,
      onQuit,
      navigate,
    ]
  );

  const casusPaths = useRecoilValue(casusPathState);

  const handleActiveTabChange = useCallback(
    (item: GlobalTabBarItem) => {
      if (pathname.startsWith(item.pathname)) {
        return;
      }
      const lastVisited = casusPaths[item.id]?.lastVisited;
      navigate(lastVisited ?? item.pathname);
    },
    [casusPaths, navigate, pathname]
  );

  const [shouldClearTabItems, setShouldClearTabItems] = useState(false);
  const handleCloseAllTabs = useCallback(() => {
    if (isCasusSubscreen(pathname)) {
      navigate(getRouteUrl({module: 'casus'}));
      // We should wait until navigation is done and content of window is changed accordingly before actually clearing
      // tabs, otherwise if the user is inside a casus and we clear tabs before that casus is unloaded, it will activate
      // the tab again and prevents clearing it.
      setShouldClearTabItems(true);
    } else {
      clearTabBarItems();
    }
  }, [navigate, pathname, clearTabBarItems]);

  useEffect(() => {
    if (!shouldClearTabItems || isCasusSubscreen(pathname)) {
      return;
    }
    clearTabBarItems();
    setShouldClearTabItems(false);
    // Only react to changes of shouldClearTabItems and pathname
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, shouldClearTabItems]);

  const backupShelfFiles = useBackupShelfFiles({
    onOpen: () => setActiveSidebar(SidebarType.BACKUP_SHELF),
  });

  return (
    <StyledHeader>
      {versionInformationDialog}
      {isReportDialogVisible ? (
        <CreateDocumentFromTemplate
          forReporting={true}
          onClose={closeReportDialog}
        />
      ) : null}
      <LeftSide>
        <CasusTabBar
          activeItem={getTabByPathname(pathname)}
          ItemComponent={CasusTab}
          items={tabBarItems}
          onActiveItemChange={handleActiveTabChange}
          onCloseAll={handleCloseAllTabs}
        />
      </LeftSide>
      {/* As soon as the spec exists, Quicksearch-field should be implemented here with Component ButtonSearchBar*/}
      <BackupShelf
        disabled={!isElectron()}
        filesList={backupShelfFiles}
        isOpen={activeSidebar === SidebarType.BACKUP_SHELF}
        setIsOpen={(shouldOpen) =>
          setActiveSidebar(shouldOpen ? SidebarType.BACKUP_SHELF : undefined)
        }
      />
      <EHeaderButton
        extraActions={userMenuActions}
        testId='version-icon-button'
        title={t('headerButtons.user')}
        warningFlag={hasFailedMailAuthentication}
      >
        <User />
      </EHeaderButton>
      <UploadQueue
        isOpen={activeSidebar === SidebarType.UPLOADING_DOCUMENTS}
        setIsOpen={(shouldOpen) =>
          setActiveSidebar(
            shouldOpen ? SidebarType.UPLOADING_DOCUMENTS : undefined
          )
        }
      />
      <FileShelf
        disabled={!isElectron()}
        isOpen={activeSidebar === SidebarType.FILE_SHELF}
        setIsOpen={(shouldOpen) =>
          setActiveSidebar(shouldOpen ? SidebarType.FILE_SHELF : undefined)
        }
      />
      <Notifications
        isOpen={activeSidebar === SidebarType.NOTIFICATIONS}
        setIsOpen={(shouldOpen) =>
          setActiveSidebar(shouldOpen ? SidebarType.NOTIFICATIONS : undefined)
        }
      />
      {shouldShowTimeTracker ? (
        <TimetrackerButton
          isOpen={activeSidebar === SidebarType.TIMETRACKER}
          setIsOpen={(shouldOpen) =>
            setActiveSidebar(shouldOpen ? SidebarType.TIMETRACKER : undefined)
          }
        />
      ) : null}
      <EHeaderButton
        extraActions={addMenuItems}
        testId='add-icon-button'
        title={t('headerButtons.create')}
      >
        <Add />
      </EHeaderButton>
    </StyledHeader>
  );
};

export const Header = observer(UHeader);
